import React from 'react';
import './DaySelectionModal.css';
import ModalOverlay from '../ModalOverlay/ModalOverlay';
import CloseButton from '../Buttons/CloseButton/CloseButton';
import Button from '../Buttons/Button/Button';

/**
 * Day information for selection
 */
export interface DayInfo {
    /** Day index (0-based) */
    dayIndex: number;
    /** Date string for display */
    date: string;
}

/**
 * Props for DaySelectionModal component
 */
interface DaySelectionModalProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback when modal should be closed */
    onClose: () => void;
    /** Callback when a day is selected */
    onSelectDay: (dayIndex: number) => void;
    /** Array of day objects to display */
    days: DayInfo[];
    /** Currently selected day index (optional, for highlighting) */
    selectedDayIndex?: number;
    /** Place name being added (for context) */
    placeName?: string;
}

/**
 * DaySelectionModal component for selecting which day to add a place to
 * Used in multi-day trips when user selects a place from search results
 */
const DaySelectionModal: React.FC<DaySelectionModalProps> = ({
    isOpen,
    onClose,
    onSelectDay,
    days,
    selectedDayIndex,
    placeName,
}) => {
    const handleDayClick = (dayIndex: number): void => {
        onSelectDay(dayIndex);
    };

    const handleCancel = (): void => {
        onClose();
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onClose={handleCancel}>
            <div className="day-selection-modal">
                <div className="day-selection-modal-header">
                    <h2 className="day-selection-modal-title">Select Day</h2>
                    <CloseButton
                        onClick={handleCancel}
                        ariaLabel="Close day selection"
                        size={24}
                    />
                </div>

                <div className="day-selection-modal-content">
                    {placeName && (
                        <p className="day-selection-modal-place-name">
                            Add &quot;{placeName}&quot; to which day?
                        </p>
                    )}
                    {!placeName && (
                        <p className="day-selection-modal-place-name">
                            Add to which day?
                        </p>
                    )}

                    <div className="day-selection-modal-days-list">
                        {days.map((day) => {
                            const isSelected = selectedDayIndex === day.dayIndex;
                            return (
                                <button
                                    key={day.dayIndex}
                                    type="button"
                                    className={`day-selection-modal-day-item ${
                                        isSelected ? 'selected' : ''
                                    }`}
                                    onClick={() => handleDayClick(day.dayIndex)}
                                    aria-label={`Select day ${day.dayIndex + 1}: ${day.date}`}
                                    aria-pressed={isSelected}
                                >
                                    <span className="day-selection-modal-day-label">
                                        Day {day.dayIndex + 1}
                                    </span>
                                    <span className="day-selection-modal-day-date">
                                        {day.date}
                                    </span>
                                    {isSelected && (
                                        <span className="day-selection-modal-day-checkmark">
                                            ✓
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="day-selection-modal-footer">
                    <Button
                        variant="secondary"
                        onClick={handleCancel}
                        className="day-selection-modal-cancel-button"
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </ModalOverlay>
    );
};

export default DaySelectionModal;

